import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import {
  initializeAuth,
  getAuth,
  browserLocalPersistence,
  browserPopupRedirectResolver,
} from "firebase/auth";
import {
  getFirestore,
  initializeFirestore,
  memoryLocalCache,
} from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import {
  firebaseConfig,
  getDatabaseId,
  attachDbHeaderInterceptor,
} from "@eventify/shared/config/firebaseConfig";
import { ALL_RUNTIME_HOSTNAMES } from "@eventify/shared/config/hostnames";
import { setFirebase } from "@eventify/shared/firebase/instance";
import { isFirestorePermissionDeniedLog } from "@eventify/shared/state/authSwapStore";

// Swallow the transient permission-denied flood that fires for ~1s after a
// venue swap (signInWithCustomToken). The Firestore SDK logs these directly
// via console.error — outside our own listener handlers — so we need to
// intercept here. The interceptor is scoped narrowly: it only swallows when
// (a) the authSwapStore.isSwapping flag is set, AND (b) the log payload
// matches "permission-denied" in a snapshot listener. Real errors at any
// other time pass through unchanged.
//
// Must install BEFORE any Firebase import runs (i.e. before initializeApp
// below), so the patched console.error is what Firestore's logger captures.
if (typeof window !== "undefined") {
  const originalError = console.error;
  console.error = (...args: unknown[]) => {
    if (isFirestorePermissionDeniedLog(args)) return;
    originalError.apply(console, args as Parameters<typeof console.error>);
  };
}

// authDomain MUST match the hosting domain serving the page, otherwise
// signInWithPopup/Redirect fails. We serve the same static bundle from multiple
// domains (dev: therunup-web.web.app, prod: therunup.ai +
// therunup-prod.web.app), so detect at runtime instead of baking in a single
// value at build time. Server-side render falls back to dev — only the browser
// ever calls auth APIs. localhost is intentionally NOT in ALL_RUNTIME_HOSTNAMES
// — Firebase Auth's popup handler is only hosted on real Firebase domains, so
// on localhost we fall through to therunup-web.web.app for the handler page.
//
// Source of truth: packages/shared/config/hostnames.ts. Add new hostnames there.
const resolvedAuthDomain =
  typeof window !== "undefined" && ALL_RUNTIME_HOSTNAMES.has(window.location.hostname)
    ? window.location.hostname
    : "therunup-web.web.app";

const webConfig = {
  ...firebaseConfig,
  authDomain: resolvedAuthDomain,
};

const app = initializeApp(webConfig);

// Firebase App Check — attests that requests come from a legit browser running
// our actual web app, not a script with a leaked API key.
//
// Currently in UNENFORCED / audit mode on the Firebase Console side for every
// API (Firestore, Functions, Storage). So today this just starts tagging
// requests with tokens; nothing is blocked yet. After ~1–2 weeks of clean
// traffic in Firebase Console → App Check → Requests, flip each service from
// Unenforced to Enforced (one at a time, Firestore first).
//
// reCAPTCHA v3 site key is PUBLIC; safe to ship in the bundle. Only ever runs
// in the browser (server-side renders have no window).
// TEMP: App Check disabled — reCAPTCHA v3 was throwing "placeholder element
// must be an element or id" and hanging signInWithPopup. App Check is in
// audit mode (not enforced) per Firebase Console, so disabling here is safe
// for now. Re-enable once the reCAPTCHA wiring is fixed.
if (typeof window !== "undefined" && false) {
  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  if (siteKey) {
    try {
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(siteKey!),
        isTokenAutoRefreshEnabled: true,
      });
    } catch (err) {
      console.warn("[firebase] App Check init failed:", err);
    }
  } else {
    console.warn("[firebase] NEXT_PUBLIC_RECAPTCHA_SITE_KEY not set — App Check disabled");
  }
}

// browserPopupRedirectResolver actively registers popup handlers at init time,
// which throws "Expected a class definition" during Next.js static export
// (Node.js, no window). Only include it client-side; signInWithPopup is only
// ever called from the browser anyway.
//
// HMR safety (same rationale as the db block below): initializeAuth throws
// "auth/already-initialized" on the second call within the same FirebaseApp.
// Fall through to getAuth(app) in that case so dev HMR doesn't break.
let auth: ReturnType<typeof getAuth>;
try {
  auth = initializeAuth(app, {
    persistence: browserLocalPersistence,
    ...(typeof window !== "undefined"
      ? { popupRedirectResolver: browserPopupRedirectResolver }
      : {}),
  });
} catch {
  auth = getAuth(app);
}
// Memory-only Firestore cache. The previous deprecated
// `enableIndexedDbPersistence()` and the newer
// `persistentLocalCache + persistentMultipleTabManager` both rely on
// IndexedDB + cross-window localStorage coordination, which intermittently
// blow up with "FIRESTORE INTERNAL ASSERTION FAILED (ID: ca9) ve:-1" when:
//   - the cache from a previous SDK version is on disk,
//   - multiple windows of the app are open,
//   - or the auth identity changes (signInWithCustomToken — the multi-venue
//     swap) while listeners are mid-flight.
// Once that assertion fires, the SDK is permanently broken in that tab —
// every read/write fails silently (e.g. PIN-verify "Approve" button does
// nothing). Memory cache trades offline support for stability: no
// IndexedDB, no cross-window coordination, no assertion. We can revisit
// persistent caching after the multi-venue work settles.
//
// Must run BEFORE any other Firestore method on this app instance.
//
// 2026-05-25: HMR safety. Dev server hot-reloads can re-evaluate this
// module. initializeFirestore throws "Firestore has already been
// started" if called twice on the same FirebaseApp — at which point
// the catch returns the already-initialized instance via
// getFirestore(app). Prod build evaluates this module exactly once,
// so the catch path is dev-only. Without this, `db` ends up undefined
// after any HMR cycle and every Firestore call crashes with
// "Expected first argument to collection() to be a CollectionReference,
// a DocumentReference or FirebaseFirestore".
const databaseId = getDatabaseId();
let db: ReturnType<typeof getFirestore>;
if (typeof window !== "undefined") {
  try {
    db =
      databaseId === "(default)"
        ? initializeFirestore(app, { localCache: memoryLocalCache() })
        : initializeFirestore(app, { localCache: memoryLocalCache() }, databaseId);
  } catch {
    db = databaseId === "(default)" ? getFirestore(app) : getFirestore(app, databaseId);
  }
} else {
  db = databaseId === "(default)" ? getFirestore(app) : getFirestore(app, databaseId);
}
const functions = getFunctions(app, "me-west1");
attachDbHeaderInterceptor(functions, databaseId);

// Register the firebase singletons with shared code so packages/shared/* can
// reach Firestore/Auth/Functions without importing this web-specific file.
setFirebase({ app, auth, db, functions });

export { app, auth, db, functions };
